import React from 'react';
import {
  MultiSelectTree,
  MultiSelectTreeChangeEvent,
  MultiSelectTreeFilterChangeEvent,
  MultiSelectTreeHandle,
  getMultiSelectTreeValue,
} from '@progress/kendo-react-dropdowns';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import { processMultiSelectTreeData } from '../../Reporting/Pages/MultiselectTree-Data-Operations';

const treeFields = {
  dataItemKey: 'id',
  checkField: 'checkField',
  checkIndeterminateField: 'checkIndeterminateField',
  expandField: 'expanded',
  subItemsField: 'items',
} as const;

export interface SelectAllMultiSelectTreeOption<T extends string = string> {
  id: T;
  text: string;
}

interface Props<T extends string> {
  options: SelectAllMultiSelectTreeOption<T>[];
  value: T[];
  onChange: (newValues: T[]) => void;
  tagLabel: string;
  selectAllLabel: string;
  width?: number | string;
  height?: number | string;
  /** Unique key for the root "Select All" id. Pass a stable string per instance ("compliance", "entity", ...). */
  groupKey: string;
  /** Show a search box inside the popup to filter options by text. Off by default. */
  filterable?: boolean;
  /** Constrain the popup to the same width as the input. Off by default (popup sizes to content). */
  matchPopupWidth?: boolean;
  /** Show one chip per selected item (default look) instead of the "(n) label" summary tag. */
  showTags?: boolean;
  /** Disable the control (e.g. while a request is in flight). Off by default. */
  disabled?: boolean;
}

export const SelectAllMultiSelectTree = <T extends string>({
  options,
  value,
  onChange,
  tagLabel,
  selectAllLabel,
  width = 175,
  height = 30,
  groupKey,
  filterable = false,
  matchPopupWidth = false,
  showTags = false,
  disabled = false,
}: Props<T>) => {
  const rootId = `__all_${groupKey}__`;
  // Controlled open state so the chevron can toggle the popup closed (Kendo's default
  // doesn't close on a second arrow click — it only closed on outside-click).
  const [opened, setOpened] = React.useState(false);
  const [filter, setFilter] = React.useState('');
  const treeRef = React.useRef<MultiSelectTreeHandle>(null);

  // Full source drives select-all detection, the value, and onChange reconciliation — so selections
  // outside the current filter are never lost. Only the rendered `treeData` is narrowed by the filter.
  const treeSource = [{ id: rootId, text: selectAllLabel, items: options }];
  const leaves = treeSource[0].items;
  const treeValue = [
    ...(value.length === leaves.length ? [treeSource[0]] : []),
    ...leaves.filter((item) => value.includes(item.id)),
  ];
  const q = filter.trim().toLowerCase();
  const displayedItems = q ? options.filter((item) => item.text.toLowerCase().includes(q)) : options;
  const displayedSource = [{ id: rootId, text: selectAllLabel, items: displayedItems }];
  const treeData = processMultiSelectTreeData(displayedSource, {
    expanded: [rootId],
    value: treeValue,
    ...treeFields,
  });

  return (
    <div style={{ position: 'relative', width }}>
      <MultiSelectTree
        ref={treeRef}
        // showTags: let the field grow with the chips (no fixed height).
        style={showTags ? { width } : { width, height }}
        className={
          showTags ? undefined : 'multi-select-custom-display multi-select-custom-display-readonly'
        }
        disabled={disabled}
        opened={opened}
        onOpen={() => setOpened(true)}
        onClose={() => {
          setOpened(false);
          setFilter('');
        }}
        filterable={filterable}
        onFilterChange={(event: MultiSelectTreeFilterChangeEvent) => setFilter(event.filter?.value ?? '')}
        data={treeData}
        value={treeValue}
        textField="text"
        dataItemKey={treeFields.dataItemKey}
        checkField={treeFields.checkField}
        checkIndeterminateField={treeFields.checkIndeterminateField}
        subItemsField={treeFields.subItemsField}
        expandField={treeFields.expandField}
        onExpandChange={() => {}}
        tags={
          showTags
            ? treeValue
                .filter((item: any) => item.id !== rootId)
                .map((item: any) => ({ text: item.text, data: [item] }))
            : [{ text: `(${value.length}) ${tagLabel}`, data: [] }]
        }
        onChange={(event: MultiSelectTreeChangeEvent) => {
          const newValue = getMultiSelectTreeValue(treeSource, {
            ...treeFields,
            ...event,
            value: treeValue,
          });
          onChange(
            newValue
              .filter((item: any) => item.id !== rootId)
              .map((item: any) => item.id as T),
          );
        }}
        popupSettings={{
          className: 'my-multiselect-popup',
          ...(matchPopupWidth ? { width } : {}),
        }}
      />
      {!showTags && (
        <span
          onMouseDown={(e) => {
            // preventDefault keeps focus stable. When opening we focus the input ourselves —
            // Kendo auto-closes an open popup that isn't focused, which caused the open→close flicker.
            e.preventDefault();
            if (disabled) return;
            if (opened) {
              setOpened(false);
            } else {
              setOpened(true);
              treeRef.current?.focus();
            }
          }}
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <SvgIcon icon={chevronDownIcon} />
        </span>
      )}
    </div>
  );
};

export default SelectAllMultiSelectTree;
